import { useAppTranslation } from '../i18n/useAppTranslation';
import { Box, Typography } from '@mui/material';

/**
 * Selecteur de note de 1 a 10 sous forme de carres cliquables.
 * Remplace le slider (input type=range), peu precis au doigt sur mobile.
 * Les carres <= a la valeur choisie sont remplis avec la couleur du critere,
 * ce qui donne une lecture immediate du niveau.
 */
type Props = {
  label: string;
  value: number;
  color: string;
  onChange: (value: number) => void;
};

export default function RatingSquares({ label, value, color, onChange }: Props) {
  const { tr } = useAppTranslation();
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="body2" gutterBottom sx={{ fontWeight: 600 }}>
        {tr(label)}{' '}
        <Box component="span" sx={{ color, fontWeight: 800 }}>({value}/10)</Box>
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => {
          const filled = n <= value;
          const selected = n === value;
          return (
            <Box
              key={n}
              role="button"
              tabIndex={0}
              aria-label={tr("{label} : note {score} sur 10", { label: tr(label), score: n })}
              onClick={() => onChange(n)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChange(n); }
              }}
              sx={{
                flex: '1 1 0',
                minWidth: 0,
                height: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                userSelect: 'none',
                borderRadius: 1,
                fontWeight: 700,
                fontSize: '0.85rem',
                border: '2px solid',
                borderColor: filled ? color : 'divider',
                bgcolor: filled ? color : 'transparent',
                color: filled ? '#fff' : 'text.secondary',
                boxShadow: selected ? `0 0 0 2px ${color}66` : 'none',
                transition: 'background-color .12s ease, border-color .12s ease, transform .12s ease',
                '&:hover': { borderColor: color, transform: 'translateY(-1px)' },
              }}
            >
              {n}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
